/**
 * Single Stone accessibility enhancements.
 *
 * Ported from the inline scripts in the live Symmetry 7.1.1 theme (layout/theme.liquid).
 * Patches that Symmetry 8.3.2 now handles natively were not carried over:
 *  - Escape closes drawers (side-drawer.js)
 *  - Predictive search result announcements (#HeaderSearchResultsStatus)
 *  - Labels on custom select buttons (snippets/custom-select.liquid)
 *  - Names for empty product description summaries (snippets/product-detail-tabs.liquid)
 *
 * All DOM-watching fixes share one MutationObserver, batched to one run per animation frame.
 */
(function () {
  'use strict';

  function createStatusRegion(id) {
    let region = document.getElementById(id);
    if (!region) {
      region = document.createElement('div');
      region.id = id;
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    return region;
  }

  /* ---------------------------------------------------------------------------------------------
   * Wishlist Hero: descriptive button label and a name for the dialog close button
   * ------------------------------------------------------------------------------------------- */
  function updateWishlistLabels() {
    const wishlistButton = document.querySelector('button[aria-label="ADD TO WISHLIST"]');
    const productTitle = document.querySelector('h1');
    if (wishlistButton && productTitle) {
      wishlistButton.setAttribute('aria-label', `Add ${productTitle.textContent.trim()} to Wishlist`);
    }

    const wishlistCloseButton = document.querySelector('.wishlist-hero-header-close');
    if (wishlistCloseButton && !wishlistCloseButton.hasAttribute('aria-label')) {
      wishlistCloseButton.setAttribute('aria-label', 'Close wishlist');
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Cart: announce "<product> added to cart." when the cart drawer opens after an add
   * ------------------------------------------------------------------------------------------- */
  let cartStatus = null;
  let pendingProductName = '';

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('form.js-product-form[action="/cart/add"]')) return;
    const productTitle = document.querySelector('h1');
    pendingProductName = productTitle ? productTitle.textContent.trim() : 'Item';
  }, true);

  function announceCartAdd() {
    if (!pendingProductName || !document.querySelector('cart-drawer[open]')) return;
    const name = pendingProductName;
    pendingProductName = '';
    cartStatus.textContent = '';
    setTimeout(() => {
      cartStatus.textContent = `${name} added to cart.`;
    }, 100);
  }

  /* ---------------------------------------------------------------------------------------------
   * Main navigation: Escape closes an open dropdown, Tab moves into an open dropdown
   * ------------------------------------------------------------------------------------------- */
  function getProxyNavItem(mainNav, itemIndex) {
    const proxyNav = mainNav.dataset.proxyNav ? document.getElementById(mainNav.dataset.proxyNav) : null;
    if (!proxyNav) return null;
    return proxyNav.querySelectorAll('.navigation__tier-1 > .navigation__item')[itemIndex] || null;
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const mainNav = document.querySelector('#main-nav');
    if (!mainNav) return;

    const openTrigger = mainNav.querySelector(
      '.navigation__tier-1 > .navigation__item > .navigation__link[aria-expanded="true"][aria-controls]'
    );
    if (!openTrigger) return;

    const openItem = openTrigger.parentElement;
    const itemIndex = Array.from(openItem.parentElement.children).indexOf(openItem);

    event.preventDefault();
    openItem.dispatchEvent(new Event('mouseleave'));

    const proxyItem = getProxyNavItem(mainNav, itemIndex);
    const proxyLink = proxyItem ? proxyItem.querySelector(':scope > .navigation__link') : null;
    if (proxyLink) {
      proxyLink.setAttribute('aria-expanded', 'false');
      proxyLink.focus();
      return;
    }

    openTrigger.focus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || event.shiftKey) return;

    const visibleTrigger = event.target.closest(
      '.navigation__tier-1 > .navigation__item--with-children > .navigation__link'
    );
    if (!visibleTrigger || visibleTrigger.getAttribute('aria-expanded') !== 'true') return;

    const mainNav = document.querySelector('#main-nav');
    if (!mainNav || mainNav.contains(visibleTrigger)) return;

    const visibleItem = visibleTrigger.parentElement;
    const itemIndex = Array.from(visibleItem.parentElement.children).indexOf(visibleItem);
    const mainItem = mainNav.querySelectorAll('.navigation__tier-1 > .navigation__item')[itemIndex];
    const dropdown = mainItem ? mainItem.querySelector('.navigation__tier-2-container') : null;
    if (!dropdown) return;

    const firstLink = dropdown.querySelector(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
    );
    if (!firstLink) return;

    event.preventDefault();
    firstLink.focus();
  });

  /* ---------------------------------------------------------------------------------------------
   * Focus trapping helper for third-party dialogs
   * ------------------------------------------------------------------------------------------- */
  const focusableSelector = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector))
      .filter((el) => el.offsetParent !== null);
  }

  function trapTab(event, container) {
    const focusable = getFocusable(container);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      event.stopPropagation();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      event.stopPropagation();
      first.focus();
    }
  }

  /* Klaviyo sign-up form modal */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const dialog = document.querySelector('[role="dialog"][aria-modal="true"][data-kl-scroll-locking-modal="true"]');
    if (dialog) trapTab(event, dialog);
  });

  /* ---------------------------------------------------------------------------------------------
   * Wholesale ("wsg") contact pop-up: dialog semantics, focus management, Escape to close
   * ------------------------------------------------------------------------------------------- */
  let wsgLastFocused = null;
  let wsgWasOpen = false;

  function getOpenWsgDialog() {
    return Array.from(document.querySelectorAll('.wsg-outer')).find((dialog) => {
      const style = getComputedStyle(dialog);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }) || null;
  }

  function prepareWsgDialog(dialog) {
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');

    if (dialog.querySelector('#wsg-contact-popup-title')) {
      dialog.setAttribute('aria-labelledby', 'wsg-contact-popup-title');
    }

    const close = dialog.querySelector('.wsg-close');
    if (close) {
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      close.setAttribute('aria-label', 'Close contact form');

      if (!close.dataset.accessibilityReady) {
        close.dataset.accessibilityReady = 'true';
        close.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            close.click();
          }
        });
      }
    }
  }

  function checkWsgDialog() {
    const dialog = getOpenWsgDialog();
    const isOpen = !!dialog;

    if (isOpen && !wsgWasOpen) {
      wsgLastFocused = document.activeElement;
      prepareWsgDialog(dialog);
      setTimeout(() => {
        const focusable = getFocusable(dialog);
        if (focusable.length) focusable[0].focus();
      }, 100);
    }

    if (!isOpen && wsgWasOpen) {
      setTimeout(() => {
        if (wsgLastFocused && typeof wsgLastFocused.focus === 'function') wsgLastFocused.focus();
      }, 100);
    }

    wsgWasOpen = isOpen;
  }

  document.addEventListener('keydown', (event) => {
    const dialog = getOpenWsgDialog();
    if (!dialog) return;

    if (event.key === 'Escape') {
      const close = dialog.querySelector('.wsg-close');
      if (close) {
        event.preventDefault();
        event.stopPropagation();
        close.click();
      }
      return;
    }

    if (event.key === 'Tab') trapTab(event, dialog);
  }, true);

  /* ---------------------------------------------------------------------------------------------
   * Collection filters: focus into the panel on open; Escape closes and returns focus
   * ------------------------------------------------------------------------------------------- */
  let filterTrigger = null;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-toggle-filters]');
    if (!trigger || trigger.classList.contains('filters__close') || trigger.classList.contains('filter-shade')) return;

    filterTrigger = trigger;
    setTimeout(() => {
      const container = document.querySelector('filter-container');
      const firstControl = container
        ? container.querySelector('.filter-group__heading[tabindex="0"], .filter-group__checkbox')
        : null;
      if (firstControl) firstControl.focus();
    }, 250);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const container = document.querySelector('filter-container');
    if (!container || !container.classList.contains('filter-container--show-filters-desktop')) return;

    const closeButton = container.querySelector('.filters__close');
    if (!closeButton) return;

    event.preventDefault();
    closeButton.click();
    setTimeout(() => {
      if (filterTrigger) filterTrigger.focus();
    }, 100);
  });

  function updateFilterAria() {
    document.querySelectorAll('toggle-target.filter-group__heading').forEach((trigger, index) => {
      const selector = trigger.getAttribute('data-toggle-target');
      const panel = selector ? document.querySelector(selector) : null;
      if (!panel) return;

      if (!panel.id) panel.id = `filter-panel-${index}`;
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-expanded', panel.classList.contains('toggle-target--hidden') ? 'false' : 'true');
    });
  }

  /* ---------------------------------------------------------------------------------------------
   * Collection grid/list layout switch: button semantics with pressed state
   * ------------------------------------------------------------------------------------------- */
  function getCurrentLayout() {
    for (const bar of document.querySelectorAll('.utility-bar')) {
      const style = getComputedStyle(bar);
      const rect = bar.getBoundingClientRect();
      const isVisible = style.visibility !== 'hidden' && style.display !== 'none'
        && parseFloat(style.opacity) > 0 && rect.width > 0 && rect.height > 0;
      if (!isVisible) continue;

      const active = bar.querySelector('.layout-switch--active');
      if (active && active.classList.contains('layout-switch--one-column')) return 'one';
      if (active && active.classList.contains('layout-switch--two-columns')) return 'two';
    }
    return null;
  }

  function updateLayoutSwitchState() {
    const switches = document.querySelectorAll('.layout-switch');
    if (!switches.length) return;

    const currentLayout = getCurrentLayout();
    switches.forEach((el) => {
      el.setAttribute('role', 'button');
      if (el.classList.contains('layout-switch--two-columns')) {
        el.setAttribute('aria-label', 'Show two products per row');
        el.setAttribute('aria-pressed', currentLayout === 'two' ? 'true' : 'false');
      } else if (el.classList.contains('layout-switch--one-column')) {
        el.setAttribute('aria-label', 'Show one product per row');
        el.setAttribute('aria-pressed', currentLayout === 'one' ? 'true' : 'false');
      }
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('.layout-switch')) setTimeout(updateLayoutSwitchState, 100);
  }, true);

  /* ---------------------------------------------------------------------------------------------
   * Links that open in a new tab: tell screen reader users
   * ------------------------------------------------------------------------------------------- */
  function labelNewTabLinks() {
    document.querySelectorAll('a[target="_blank"]').forEach((link) => {
      if (link.href.includes('shopify.com?utm_campaign=poweredby')) return;

      const existingLabel = link.getAttribute('aria-label');
      const hiddenText = link.querySelector('.visually-hidden');

      if (existingLabel) {
        if (!existingLabel.includes('opens in a new tab')) {
          link.setAttribute('aria-label', `${existingLabel} — opens in a new tab`);
        }
      } else if (hiddenText) {
        const text = hiddenText.textContent.trim();
        if (!text.includes('opens in a new tab')) {
          hiddenText.textContent = `${text} — opens in a new tab`;
        }
      }
    });
  }

  /* ---------------------------------------------------------------------------------------------
   * Floating "Get in Touch" contact widget: expose it as a button
   * ------------------------------------------------------------------------------------------- */
  function fixContactWidgetRole() {
    document.querySelectorAll('div[tabindex="0"]:not([role])').forEach((el) => {
      if ((el.textContent || '').trim() === 'Get in Touch') el.setAttribute('role', 'button');
    });
  }
/* --------------------------------------------------------------------------
 * Klaviyo "Join the Club" launcher
 * -------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('#klaviyo-join-club-trigger');
  if (!trigger) return;

  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push([
    'openForm',
    trigger.dataset.klaviyoFormId || 'UKXJda'
  ]);
});
/* Dismiss custom Join the Club launcher */
document.addEventListener('click', (event) => {
  const dismiss = event.target.closest('#klaviyo-join-club-dismiss');
  if (!dismiss) return;

  event.preventDefault();
  event.stopPropagation();

  try {
    sessionStorage.setItem('ssla-join-club-dismissed', 'true');
  } catch (e) {}

  const container = document.getElementById('klaviyo-join-club-container');
  if (container) container.remove();
});

/* Keep Join the Club dismissed across pages in this tab */
function rememberJoinClubDismissal() {
  try {
    if (sessionStorage.getItem('ssla-join-club-dismissed') === 'true') {
      document.getElementById('klaviyo-join-club-container')?.remove();
    }
  } catch (e) {}
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', rememberJoinClubDismissal);
} else {
  rememberJoinClubDismissal();
}
/* Hide Klaviyo's native teaser — custom launcher is used instead */
function hideKlaviyoNativeTeaser() {
  document.querySelectorAll('[data-testid="animated-teaser"]').forEach((teaser) => {
    const klaviyoTeaser = teaser.closest('[class*="kl-teaser-"]');
    if (klaviyoTeaser) {
      klaviyoTeaser.style.display = 'none';
    }
  });
}

  /* ---------------------------------------------------------------------------------------------
   * Shared observer
   * ------------------------------------------------------------------------------------------- */
  function runAll() {
    updateWishlistLabels();
    announceCartAdd();
    checkWsgDialog();
    updateFilterAria();
    updateLayoutSwitchState();
    labelNewTabLinks();
    fixContactWidgetRole();
    // hideKlaviyoNativeTeaser();

  }

  let scheduled = false;
  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      runAll();
    });
  }

  function init() {
    cartStatus = createStatusRegion('cart-accessibility-status');
    runAll();
    new MutationObserver(scheduleRun).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'open']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
